import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { MENU_VISIBLE_OPTIONS, computeVisibleCount, splitTabs, type AdminTabItem } from './adminTabsLayout';

interface AdminTabsProps {
  items: AdminTabItem[];
  activeKey: string;
  onSelect: (key: string) => void;
  /** Absent in View mode: nothing can be added or discarded there. */
  onAdd?: (() => void) | undefined;
  /** Shown as an "x" on the unsent draft tab only. */
  onDiscardDraft?: (() => void) | undefined;
}

const GAP = 6;
const MENU_ROW_HEIGHT = 36;

/**
 * Figma S01-S03: one tab per Admin, with "+ Add Admin" after them. Tabs that do not fit collapse into "+N More" (N is the real number
 * hidden) whose menu shows five Admins before it scrolls and ends with Add Admin. The active tab always stays visible. The menu is keyboard
 * operable: Enter/Space/ArrowDown open it, arrows/Home/End move, Escape closes and returns focus to the button.
 */
export function AdminTabs({ items, activeKey, onSelect, onAdd, onDiscardDraft }: AdminTabsProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const moreButtonRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  const [visibleCount, setVisibleCount] = useState(items.length);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuItemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const pendingFocus = useRef<number | null>(null);

  const measure = useCallback(() => {
    const container = containerRef.current;
    const probe = measureRef.current;
    if (!container || !probe) return;
    const widths = Array.from(probe.querySelectorAll<HTMLElement>('[data-measure="tab"]')).map((el) => el.offsetWidth);
    // A small allowance for the icon and the arrow that the measuring copies show as plain text.
    const more = (probe.querySelector<HTMLElement>('[data-measure="more"]')?.offsetWidth ?? 0) + 8;
    const add = (probe.querySelector<HTMLElement>('[data-measure="add"]')?.offsetWidth ?? 0) + 14;
    setVisibleCount(computeVisibleCount(widths, container.clientWidth, more, add, GAP));
  }, []);

  // Re-measure whenever the labels change (names update as they are typed) and whenever the container is resized.
  const labelSignature = items.map((i) => `${i.key}:${i.label}`).join('|');
  useLayoutEffect(() => {
    measure();
  }, [measure, labelSignature, onAdd]);
  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => measure());
    observer.observe(container);
    return () => observer.disconnect();
  }, [measure]);

  const activeIndex = Math.max(0, items.findIndex((i) => i.key === activeKey));
  const { visible, hidden } = useMemo(() => splitTabs(items.length, visibleCount, activeIndex), [items.length, visibleCount, activeIndex]);

  // The menu is only ever open while something is hidden (it closes by itself once the window is wide enough for every tab).
  const menuShown = menuOpen && hidden.length > 0;

  // Click outside closes the menu.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) setMenuOpen(false);
    };
    // Escape closes the menu wherever focus is (the menu's own handler also returns focus to the button).
    const onEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onEscape);
    };
  }, [menuOpen]);

  const menuEntryCount = hidden.length + (onAdd ? 1 : 0);
  const focusEntry = (index: number) => {
    const target = menuItemRefs.current[(index + menuEntryCount) % menuEntryCount];
    target?.focus();
  };
  const openMenu = (focusIndex: number) => {
    pendingFocus.current = focusIndex;
    setMenuOpen(true);
  };
  // Focus moves into the menu once it has actually rendered.
  useEffect(() => {
    if (menuOpen && pendingFocus.current !== null) {
      focusEntry(pendingFocus.current);
      pendingFocus.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuOpen]);
  const closeMenu = (returnFocus: boolean) => {
    setMenuOpen(false);
    if (returnFocus) moreButtonRef.current?.focus();
  };

  const onMoreKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      openMenu(0);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      openMenu(menuEntryCount - 1);
    } else if (event.key === 'Escape') {
      closeMenu(false);
    }
  };

  const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const current = menuItemRefs.current.findIndex((el) => el === document.activeElement);
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      focusEntry(current + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      focusEntry(current - 1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      focusEntry(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      focusEntry(menuEntryCount - 1);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      closeMenu(true);
    } else if (event.key === 'Tab') {
      closeMenu(false);
    }
  };

  const renderTab = (item: AdminTabItem) => {
    const active = item.key === activeKey;
    const dot = item.kind === 'pending' ? <span className="tab-dot pending" aria-hidden="true" /> : item.kind === 'draft' ? <span className="tab-dot draft" aria-hidden="true" /> : null;
    if (item.kind === 'draft' && onDiscardDraft) {
      return (
        <span key={item.key} className={`admin-tab admin-tab-draft-wrap${active ? ' active' : ''}`}>
          <button type="button" className="admin-tab-select" aria-current={active ? 'true' : undefined} onClick={() => onSelect(item.key)}>
            {dot}
            {item.label}
          </button>
          <button type="button" className="admin-tab-discard" aria-label="Discard this new Admin" onClick={onDiscardDraft}>
            ×
          </button>
        </span>
      );
    }
    return (
      <button key={item.key} type="button" className={`admin-tab${active ? ' active' : ''}`} aria-current={active ? 'true' : undefined} onClick={() => onSelect(item.key)}>
        {dot}
        {item.label}
      </button>
    );
  };

  return (
    <div className="admin-tabs" ref={containerRef} role="group" aria-label="Admins">
      {visible.map((i) => items[i]).filter((i): i is AdminTabItem => i !== undefined).map(renderTab)}

      {hidden.length > 0 && (
        <div className="admin-more-wrap">
          <button
            ref={moreButtonRef}
            type="button"
            className="admin-tab admin-more"
            aria-haspopup="menu"
            aria-expanded={menuShown}
            aria-controls={menuShown ? menuId : undefined}
            onClick={() => (menuShown ? closeMenu(false) : openMenu(0))}
            onKeyDown={onMoreKeyDown}
          >
            +{hidden.length} More <i className="fa fa-angle-down" aria-hidden="true" />
          </button>
          {menuShown && (
            <div id={menuId} className="admin-more-menu" role="menu" aria-label="More Admins" onKeyDown={onMenuKeyDown}>
              <div className="admin-more-list" style={{ maxHeight: MENU_VISIBLE_OPTIONS * MENU_ROW_HEIGHT }}>
                {hidden.map((index, position) => {
                  const item = items[index];
                  if (!item) return null;
                  return (
                    <button
                      key={item.key}
                      ref={(el) => { menuItemRefs.current[position] = el; }}
                      type="button"
                      role="menuitem"
                      className="admin-more-item"
                      onClick={() => { closeMenu(false); onSelect(item.key); }}
                    >
                      {item.kind !== 'member' && <span className={`tab-dot ${item.kind}`} aria-hidden="true" />}
                      {item.label}
                    </button>
                  );
                })}
              </div>
              {onAdd && (
                <button
                  ref={(el) => { menuItemRefs.current[hidden.length] = el; }}
                  type="button"
                  role="menuitem"
                  className="admin-more-item admin-more-add"
                  onClick={() => { closeMenu(false); onAdd(); }}
                >
                  <i className="fa fa-plus-circle bps-icon" aria-hidden="true" /> Add Admin
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* With a lot of Admins the Add action lives in the "+N More" menu instead of taking tab space. */}
      {onAdd && hidden.length === 0 && (
        <button type="button" className="admin-tab add" onClick={onAdd}>
          <i className="fa fa-plus-circle bps-icon" aria-hidden="true" />
          Add Admin
        </button>
      )}

      {/* Off-screen copy of every tab, the More button and the Add button, used only to measure their natural widths. The text comes from a CSS
          `content: attr(data-label)` so it is never real text in the page (screen readers and text queries do not see a second copy). */}
      <div className="admin-tabs-measure" ref={measureRef} aria-hidden="true">
        {items.map((item) => (
          <button key={item.key} type="button" tabIndex={-1} data-measure="tab" data-label={`${item.label}${item.kind === 'draft' && onDiscardDraft ? '  ×' : ''}`} className={`admin-tab${item.kind === 'member' ? '' : ' with-dot'}`} />
        ))}
        <button type="button" tabIndex={-1} data-measure="more" data-label={`+${Math.max(items.length, 1)} More ▾`} className="admin-tab admin-more" />
        <button type="button" tabIndex={-1} data-measure="add" data-label="+ Add Admin" className="admin-tab add" />
      </div>
    </div>
  );
}
